/**
 * The page a visitor with no account sees at grow.gardengameplan.com: what
 * Grow is, what it costs, and a button that starts a garden. Every section
 * is its own small component (sections/), put in order by landingSections;
 * this file adds the header (the mark, "Gardens" to Gardens on Grow at
 * /gardens, and "Sign in") above them and the foot
 * (Terms, Privacy, Sign in, the language switch) below.
 *
 * Its buttons lead into the sign-in page (LoginPage) that was the first
 * screen before: "Start your garden free" and the others open it on signing
 * up, "Sign in" on signing in. A founding link (?founding=CODE) keeps its
 * code (arrivalSource) and shows the founding welcome; the sign-up it leads
 * to sends the code as before. The first line follows the ad
 * (?ad=dinner, ?ad=frost), or, for a visitor from the owner's own Instagram
 * or Facebook link (?from=ig, ?from=fb, or kept from an earlier visit by
 * arrivalSource), says her own words, and the trial's days on the page are
 * the follower trial's (trial.FOLLOWER_DAYS), which the backend gives the
 * account they sign up.
 *
 * The remodelled page (LANDING_SECTIONS_V2) shows instead of the current one
 * only when the Grow setting landing.V2 is on (the server sends it with the
 * facts as v2, so the app has no default of its own and the page drawn is the
 * one the owner chose), or the address has ?landing=v2 (the owner's review);
 * see landingVersion.
 *
 * The prices, the phase, the trial's days, Free's plants, the founding
 * welcome, the founding places, the owner's garden address and the price
 * cards' lines come from the server (GET /billing/landing, getLandingFacts;
 * architecture audit batch 8), decided by its clock from the Grow settings,
 * so they follow the owner's panel and the calendar and match the subscribe
 * page. The founding code goes with the read only on a visit that came by a
 * founding link; the follower's link (ig or fb) goes too, and the page's language (the founder price comes back said whole
 * in it, C101; a switch of language asks again and the page stays drawn meanwhile). The sections are
 * drawn once the answer is in (the header shows meanwhile), so no price or
 * day is ever shown that the server did not say. A read that fails on our
 * side is reported to Sentry and says the standard sentence with
 * hello@gardengameplan.com, never "try again" (ServerFailureNote).
 *
 * Mounted by: Layout, in place of the sign-in page, for a visitor signed out
 * at "/" who did not come to start a garden by a share's or a garden page's
 * link (showsLanding).
 *
 * API: GET /billing/landing (getLandingFacts), cached under
 * LANDING_FACTS_QUERY_KEY for the visit.
 * Words (en/es): landing.*; auth.otherLanguage (the language switch);
 * serverFailure.message (a failed read).
 *
 * @module LandingPage
 */

import { retryRule } from '@/config/queryRetry';
import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { PublicHeader } from '@/shared/components/layout/PublicHeader';
import { ServerFailureNote } from '@/shared/components/ServerFailure';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { foundingThisVisit, keptFoundingCode, keptFrom } from '@/shared/lib/arrivalSource';
import { chooseStartLanguage } from '@/shared/lib/startLanguage';
import { getLandingFacts } from '@/shared/api/billing';
import { PRIVACY_URL, TERMS_URL } from '@/config/tunables/shared';
import { arrivalOf, isFollower } from './lib/landingFacts';
import { showsLandingV2 } from './lib/landingVersion';
import { LANDING_SECTIONS, LANDING_SECTIONS_V2 } from './landingSections';

/** Where a visitor goes from here: the sign-in page, on signing in or on signing up. */
export type LandingEntry = 'signIn' | 'signUp';

/** What the page needs from its parent. */
interface LandingPageProps {
  /** Opens the sign-in page on the way the visitor chose. */
  onEnter: (entry: LandingEntry) => void;
}

/** The React Query key for the landing page's facts; the founding code and the follower's link are added to it. */
const LANDING_FACTS_QUERY_KEY = ['landing', 'facts'] as const;

/** A link-like button in the page's small text. */
const TEXT_BUTTON = 'font-semibold text-primary underline-offset-4 hover:underline';

/**
 * The landing page.
 *
 * @param props.onEnter - Called with 'signUp' by every start button and 'signIn' by "Sign in".
 */
export default function LandingPage({ onEnter }: LandingPageProps): JSX.Element {
  const { reactTranslate: t, i18n: { language } } = useTranslation();
  // The page's language, held so the switch redraws it at once.
  const [, setShownLanguage] = useState(language);
  // The ad or the owner's link that brought them, read once as the page opens.
  const [arrival] = useState(() => arrivalOf(window.location.search, keptFrom()));
  // The founding welcome only on a visit that came by a founding link; a code kept from long ago no longer outranks ?from= or ?ad=.
  const [foundingCode] = useState(() => (foundingThisVisit() ? keptFoundingCode() : null));
  const from = isFollower(arrival) ? arrival : null;
  // What the page says about price, the trial, Free and founding: the server's, by its clock.
  const { data: facts, error: factsError } = useQuery({
    queryKey: [...LANDING_FACTS_QUERY_KEY, foundingCode, from, language],
    queryFn: () => getLandingFacts({ foundingCode, from }),
    staleTime: 5 * 60 * 1000,
    retry: retryRule(1),
    // The language switch asks again (the founder price comes back said in it); the page stays drawn meanwhile.
    placeholderData: keepPreviousData,
  });
  // The remodelled page, when the setting (sent with the facts) is on or the address asks for it (for review).
  const v2 = showsLandingV2(facts?.v2 === true, window.location.search);
  const sections = v2 ? LANDING_SECTIONS_V2 : LANDING_SECTIONS;

  /** Switches the page between English and Spanish, kept on this device for sign-up. */
  const switchLanguage = (): void => {
    const next = language.startsWith('es') ? 'en' : 'es';
    chooseStartLanguage(next);
    setShownLanguage(next);
  };

  /** Opens sign-up: every "start" button. */
  const start = (): void => onEnter('signUp');

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/*
        * The header every public page wears (PublicHeader): "Grow by Garden Gameplan" (owner,
        * 2026-10-14: "yes change it to grow by garden gameplan on the page"), Gardens, Sign in and
        * Start your garden, opening this page's own sign-in and sign-up. The sentences below keep
        * "Grow" as the short name.
        */}
      <PublicHeader onSignIn={() => onEnter('signIn')} onStart={start} />
      <main className={facts ? undefined : 'min-h-[60vh] px-4 py-10 md:px-8'}>
        {facts
          ? sections.filter((entry) => !entry.isShown || entry.isShown()).map(({ id, Component }) => (
            <Component key={id} arrival={arrival} facts={facts} onStart={start} v2={v2} />
          ))
          // Nothing is said until the server has; a failure is reported and says how to reach us.
          : factsError ? <ServerFailureNote error={factsError} where="Landing" className="mx-auto max-w-prose text-center" /> : null}
      </main>
      <footer className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-t px-4 py-5 text-sm text-muted-foreground">
        <span>{t('landing.foot.tagline')}</span>
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className={TEXT_BUTTON}>{t('landing.foot.terms')}</a>
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className={TEXT_BUTTON}>{t('landing.foot.privacy')}</a>
        <button type="button" onClick={() => onEnter('signIn')} className={TEXT_BUTTON}>{t('landing.signIn')}</button>
        {/* In the other language: "Español" on the English page, "English" on the Spanish. */}
        <button type="button" onClick={switchLanguage} className={TEXT_BUTTON} lang={language.startsWith('es') ? 'en' : 'es'}>
          {t('auth.otherLanguage')}
        </button>
      </footer>
    </div>
  );
}
